/* =======================================================================
   rental-gpe.js — unified UI behavior for Gap Power rentals
   - Single “Add to Cart” pill with ✓ Added state (no duplicate chip)
   - Mobile: NO drawer (permanent sticky/“toast” experience)
   - Desktop: side drawer that pushes content; no dim/blur overlay
   - Drawer closes on X, Clear All, Add more, and when empty
   - Drawer item image/title link back to the product page
   - Sticky bar + toast + utility-bar count, cross-tab syncing
   - Safe localStorage fallback; debounced MutationObserver
   ======================================================================= */

(() => {
  'use strict';

  /* ---------------- env & config ---------------- */
  const IS_EDITOR = !!(window.Shopify && Shopify.designMode);
  const STORAGE_KEY = 'selectedRentalItems';
  const RENTAL_CART_URL = '/pages/rental-cart';
  const IS_DESKTOP = () => window.matchMedia('(min-width: 1024px)').matches;

  // Mobile UI mode: 'toast' disables the drawer on mobile entirely
  const MOBILE_UI_MODE = 'toast'; // 'toast' | 'sheet' (we're using 'toast' per request)
  // Desktop pushes page content over when drawer opens
  const DESKTOP_PUSH_CONTENT = true;
const isRentalCartPage = () => {
  return window.location.pathname === '/pages/rental-cart' || 
         window.location.pathname.includes('rental-cart');
};

/* -------- SKU/Variant helpers & audit -------- */
function _findSkuVariantNearby(node) {
  const read = (el) => (!el ? {} : {
    sku:       el.dataset?.sku || el.getAttribute?.('data-sku') || null,
    variantId: el.dataset?.variantId || el.getAttribute?.('data-variant-id') || null,
  });

  let sku = null, variantId = null, p = node;
  for (let i = 0; i < 5 && p && (!sku || !variantId); i++, p = p.parentElement) {
    const got = read(p);
    sku       = sku       || got.sku;
    variantId = variantId || got.variantId;
  }

  // Product page fallback
  if (window.product) {
    sku       = sku       || window.product?.selected_or_first_available_variant?.sku || window.product?.variants?.[0]?.sku || null;
    variantId = variantId || String(window.product?.selected_or_first_available_variant?.id || window.product?.variants?.[0]?.id || '') || null;
  }
  return { sku, variantId };
}

function _ensureSkuVariant(item, btnOrEl, { warn = true } = {}) {
  let { sku, variantId } = item || {};
  if (!sku || !variantId) {
    const fromDom = _findSkuVariantNearby(btnOrEl || document.querySelector(`[data-product-id="${item?.id}"],[data-handle="${item?.handle}"]`));
    sku       = sku       || fromDom.sku;
    variantId = variantId || fromDom.variantId;
  }
  const out = { ...item, sku: sku || null, variantId: variantId || null };

  if ((!out.sku || !out.variantId) && warn) {
    try { console.warn('[rental-gpe] Missing SKU or variantId on add', out); } catch {}
    try { 
      // use your existing toast if present; otherwise fall back to alert.
      if (typeof toast === 'function') toast('⚠ Missing SKU/variant — not captured.'); 
      else alert('⚠ Missing SKU/variant — not captured.');
    } catch {}
  }
  return out;
}

function _auditBasket(context = '') {
  try {
    const b = getBasket();
    const bad = b.filter(i => !i?.sku || !i?.variantId);
    if (bad.length) {
      console.warn(`[rental-gpe] ${bad.length} item(s) missing SKU/variant after ${context}`, bad);
      if (typeof toast === 'function') toast(`⚠ ${bad.length} item(s) missing SKU/variant.`, 5000);
    }
  } catch {}
}

  /* ---------------- safe storage ---------------- */
  function getStore() {
    try {
      const t='__ls_test__'; localStorage.setItem(t,'1'); localStorage.removeItem(t); return localStorage;
    } catch {
      const m={}; return { getItem:k => (k in m? m[k]:null), setItem:(k,v)=>{m[k]=String(v);}, removeItem:k=>{delete m[k];} };
    }
  }
  const store = getStore();
  const safeParse = (txt, fallback) => { try { return JSON.parse(txt); } catch { return fallback; } };
  const getBasket = () => {
    const raw = store.getItem(STORAGE_KEY);
    const val = safeParse(raw, []);
    return Array.isArray(val) ? val : [];
  };
  const setBasket = (arr) => { try { store.setItem(STORAGE_KEY, JSON.stringify(arr)); } catch {} };
  const totalQty = (arr = getBasket()) => arr.reduce((s, i) => s + (i?.quantity || 1), 0);

  // Cleanup for historic bad values
  try {
    const v = store.getItem(STORAGE_KEY);
    if (v === 'null' || v === 'undefined') store.removeItem(STORAGE_KEY);
  } catch {}

  /* ---------------- toast ---------------- */
  const ensureToast = () => {
    let t = document.getElementById('rental-toast');
    if (!t) {
      t = document.createElement('a');
      t.id = 'rental-toast';
      t.href = RENTAL_CART_URL;
      t.setAttribute('aria-live', 'polite');
      t.innerHTML = '<span class="toast-text"></span>';
      document.body.appendChild(t);
    }
    return t;
  };
  const toast = (txt, count) => {
    const t = ensureToast();
    t.querySelector('.toast-text').textContent = `${txt} • ${count} in cart →`;
    t.classList.add('show');
    clearTimeout(t._hide);
    t._hide = setTimeout(() => t.classList.remove('show'), 6000);
  };

  /* ---------------- drawer ---------------- */
  let drawer, backdrop, listEl;

function addBodyPush(enable) {
  if (!DESKTOP_PUSH_CONTENT || !IS_DESKTOP()) return;
  
  // Just toggle the open class - no need for rental-push
  document.body.classList.toggle('rental-drawer-open', !!enable);
}
  // Backdrop interactivity & visibility
  function updateBackdropInteractivity() {
    if (!backdrop) return;

    if (IS_DESKTOP()) {
      // Desktop = no dim/blur overlay and clicks pass through
      backdrop.classList.remove('open');
      backdrop.classList.add('pass');
    } else {
      // Mobile: only show overlay if we ever enable a sheet mode
      if (MOBILE_UI_MODE === 'sheet' && drawer?.classList.contains('open')) {
        backdrop.classList.add('open');
        backdrop.classList.remove('pass');
      } else {
        backdrop.classList.remove('open');
        backdrop.classList.remove('pass');
      }
    }
  }

  // Create drawer DOM once


const ensureDrawer = () => {
  if (!drawer) {
    backdrop = document.createElement('div');
    backdrop.id = 'rental-drawer-backdrop';
    backdrop.addEventListener('click', () => closeDrawer());

    drawer = document.createElement('aside');
    drawer.id = 'rental-drawer';
    drawer.innerHTML = `
      <header class="rd-head">
        <strong>Rental Cart</strong>
        <button class="rd-close" aria-label="Close">×</button>
      </header>
      <div class="rd-body"><ul class="rd-list"></ul></div>
      <footer class="rd-foot">
        <a class="rd-cta" href="${RENTAL_CART_URL}">View / Submit Request</a>
        <button class="rd-clear" type="button">Clear All</button>
      </footer>
    `;
    document.body.appendChild(backdrop);
    document.body.appendChild(drawer);

    drawer.querySelector('.rd-close').addEventListener('click', () => closeDrawer());
    drawer.querySelector('.rd-clear').addEventListener('click', () => {
      setBasket([]);
      updateAllUI();
      closeDrawer();
    });
  }

  // Always (re)query in case it wasn’t set yet
  listEl = drawer.querySelector('.rd-list');
  return drawer;
};




  // Open drawer (desktop only; mobile depends on MOBILE_UI_MODE)
  const openDrawer = () => { 
    if (isRentalCartPage()) return;

    ensureDrawer(); updateDrawer();

    if (!IS_DESKTOP()) {
      // MOBILE
      if (MOBILE_UI_MODE === 'sheet') {
        drawer.classList.add('open');
        drawer.removeAttribute('aria-hidden');
      }
        if (!IS_DESKTOP() && MOBILE_UI_MODE === 'toast') {
    return;} // Do nothing - mobile uses toast only
      // MOBILE_UI_MODE === 'toast' → do nothing (permanent sticky only)
      updateBackdropInteractivity();
      return;
    }

    // DESKTOP
    drawer.classList.add('open');
    drawer.removeAttribute('aria-hidden');
    addBodyPush(true);
    updateBackdropInteractivity();
  };

  // Force-close drawer
  const closeDrawer = (fromUser = false) => {
    if (!drawer) return;

    // If mobile is 'toast', keep it fully closed regardless of source
    if (!IS_DESKTOP() && MOBILE_UI_MODE === 'toast') {
      drawer.classList.remove('open');
      drawer.setAttribute('aria-hidden', 'true');
      backdrop.classList.remove('open', 'pass');
      addBodyPush(false);
      return;
    }

    // If mobile were using 'sheet', or we're on desktop:
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    backdrop.classList.remove('open', 'pass');
    addBodyPush(false);
    updateBackdropInteractivity();

    // Safety: if user explicitly closed, don't immediately re-open anywhere
    if (fromUser) _suppressNextAutoOpen = Date.now();
  };

  let _suppressNextAutoOpen = 0;

  const escapeHTML = (s) => String(s).replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));
  const updateDrawer = () => {
    ensureDrawer();
    const items = getBasket();
    listEl.innerHTML = items.length ? '' : '<li class="rd-empty">Your rental cart is empty.</li>';
    for (const it of items) {
      const imgSrc = it.imageUrl || '';
      const link = it.handle ? `/products/${encodeURIComponent(it.handle)}` : '#';
      const li = document.createElement('li');
      li.className = 'rd-row';
      li.innerHTML = `
        <a class="rd-thumb-link" href="${link}" aria-label="View ${escapeHTML(it.title || it.handle || it.id)}">
          <img class="rd-thumb" src="${imgSrc}" alt="" onerror="this.style.display='none'">
        </a>
        <div class="rd-meta">
          <a class="rd-title" href="${link}">${escapeHTML(it.title || it.handle || it.id)}</a>
        </div>
        <button class="rd-remove" data-id="${it.id}" aria-label="Remove">Remove</button>
      `;
      listEl.appendChild(li);
    }
  };

  // Keep interactivity right on resize
  const mq = window.matchMedia('(min-width: 1024px)');
  function handleMQChange() {
    // Always close when crossing breakpoints to avoid stuck state
    drawer?.classList.remove('open');
    drawer?.setAttribute('aria-hidden', 'true');
    backdrop?.classList.remove('open', 'pass');
    addBodyPush(false);
    updateBackdropInteractivity();
  }
  try { mq.addEventListener('change', handleMQChange); }
  catch { mq.onchange = handleMQChange; }

  /* ---------------- sticky bar ---------------- */
  let sticky = null;
  const ensureSticky = () => {
    if (sticky) return sticky;
    sticky = document.createElement('div');
    sticky.id = 'rental-sticky';
    sticky.innerHTML = `
      <div class="summary"></div>
      <div class="preview"></div>
      <a class="cta" href="${RENTAL_CART_URL}">View Rental Cart</a>
    `;
    sticky.addEventListener('click', (e) => {
      if (!e.target.closest('a')) {
        e.preventDefault?.();
        if (IS_DESKTOP()) openDrawer();
        else if (MOBILE_UI_MODE === 'sheet') openDrawer();
        else window.location.href = RENTAL_CART_URL; // mobile toast mode
      }
    });
    document.body.appendChild(sticky);
    return sticky;
  };
  const updateSticky = () => {
    const items = getBasket();
    ensureSticky();
    if (!items.length) { sticky.classList.remove('show'); return; }
    const qty = totalQty(items);
    sticky.querySelector('.summary').textContent = `${qty} item${qty>1?'s':''} in Rental Cart`;
    const names = items.slice(0,2).map(i => i.title || i.handle || i.id);
    sticky.querySelector('.preview').textContent =
      names.length ? names.join(' • ') + (items.length>2 ? ` • +${items.length-2} more` : '') : '';
    sticky.classList.add('show');
  };

  /* ---------------- utility-bar counters ---------------- */
  const cartIcon = '<svg width="15" height="14" viewBox="0 0 25 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 0a1 1 0 0 0 0 2h.34c.27 0 .66.24.77.83l2.9 15.37a1 1 0 0 0 1.23.78h12.48c.21 0 .4.01.59.02.1 0 .19.02.28.02.29.01.46 0 .57-.02l.08-.03 1.47-8.63c.28-1.23-.69-2.43-1.98-2.43H4.75L4.08 2.46C3.86 1.23 2.76 0 1.34 0H1Zm4.13 8h15.86l-1.56 8.96-.03.1-.02.01H6.82L5.13 8Zm3.63 14.43A1.57 1.57 0 1 0 9.37 24a1.57 1.57 0 0 0-.61-3.04Zm8.64 3.57a1.57 1.57 0 1 0 0-3.13 1.57 1.57 0 0 0 0 3.13Z"/></svg>';
  const labelHTML = (txt, qtyAttr, qty) =>
    `<span style="display:inline-flex;align-items:center;">${cartIcon}<span style="margin-left:5px;">${txt}&nbsp;(${qtyAttr ? '<span '+qtyAttr+'>'+qty+'</span>' : qty})</span></span>`;

  const refreshHeaderCounters = () => {
    const hdr = document.querySelector('[data-rental-cart-count]');
    if (hdr) hdr.textContent = String(totalQty());

    document.querySelectorAll('.utility-bar__menu-link').forEach(link => {
      const t = (link.textContent || '').trim();
      if (t === 'Rental Cart') {
        link.innerHTML = labelHTML('Rental&nbsp;Cart', 'data-rental-cart-count', totalQty());
        if (!link.dataset.rentalDrawerBound) {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            if (IS_DESKTOP()) openDrawer();
            else if (MOBILE_UI_MODE === 'sheet') openDrawer();
            else window.location.href = RENTAL_CART_URL;
          });
          link.dataset.rentalDrawerBound = '1';
        }
      }
    });
  };

  /* ---------------- add button: single source of truth ---------------- */
  const PLUS_SVG  = '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5c.55 0 1 .45 1 1v5h5a1 1 0 1 1 0 2h-5v5a1 1 0 1 1-2 0v-5H6a1 1 0 1 1 0-2h5V6c0-.55.45-1 1-1z"/></svg>';
  const CHECK_SVG = '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.29 6.7a1 1 0 0 1 .01 1.41l-9.2 9.26a1 1 0 0 1-1.42 0L3.7 11.6a1 1 0 1 1 1.42-1.41l4.06 4.05 8.48-8.55a1 1 0 0 1 1.63.01z"/></svg>';

  function renderAddBtn(btn, inCart) {
    let isAvail;
    if (btn.dataset.avail != null) {
      const v = (btn.dataset.avail + '').toLowerCase();
      isAvail = v === 'true' || v === '1';
    } else if (btn.classList.contains('avail-ok') || btn.classList.contains('avail-hold')) {
      isAvail = btn.classList.contains('avail-ok');
    } else {
      const badge = btn.closest('.rental-card-header')?.querySelector('.rental-badge');
      isAvail = !!badge?.classList.contains('available');
    }

    btn.classList.add('rental-add');
    btn.classList.toggle('avail-ok',  !!isAvail);
    btn.classList.toggle('avail-hold', !isAvail);
    btn.classList.toggle('is-added',  !!inCart);
    btn.setAttribute('aria-pressed', inCart ? 'true' : 'false');

  btn.innerHTML = inCart
    ? `<span class="rental-add__icon">${CHECK_SVG}</span><span class="rental-add__text">Added</span>`
    : `<span class="rental-add__icon">${PLUS_SVG}</span><span class="rental-add__text">Add</span>`;
}

  const computeId = (btn) =>
    btn.dataset.id ||
    btn.getAttribute('data-id') ||
    btn.closest('[data-product-id]')?.getAttribute('data-product-id') ||
    btn.dataset.handle || null;

  function paintAddButtons() {
    const inBasket = new Set(getBasket().map(i => i.id));
    document.querySelectorAll('.rental-add').forEach(btn => {
      const id = computeId(btn);
      if (!id) return;
      renderAddBtn(btn, inBasket.has(id));
    });
  }

  function clusterActions() {
    document.querySelectorAll('.rental-card-header').forEach(hdr => {
      hdr.querySelectorAll('.rental-add-tag')?.forEach(n => n.remove());

      let btn = hdr.querySelector('.rental-add');
      if (!btn) {
        btn = document.createElement('button');
        btn.className = 'rental-add';
        hdr.appendChild(btn);
      }
      const id = computeId(btn);
      if (!id) return;
      const present = new Set(getBasket().map(i => i.id)).has(id);
      renderAddBtn(btn, present);
    });
  }

  function pinDrawerIfDesktopHasItems() {
    // Avoid reopening right after a manual close
    const suppress = Date.now() - _suppressNextAutoOpen < 1000;
    if (IS_DESKTOP() && getBasket().length && !suppress) openDrawer();
  }

  const addOrRemove = (btn) => {
    const id = computeId(btn);
    if (!id) { console.warn('[rental-gpe] .rental-add missing data-id'); return; }

    const basket = getBasket();
    const idx = basket.findIndex(i => i.id === id);

    if (idx === -1) {
  const fallbackImg = btn.dataset.image || btn.closest('article,li,.product-item,.card')?.querySelector('img')?.src || '';
  const raw = {
    id:        computeId(btn),
    variantId: btn.dataset.variantId || null,
    sku:       btn.dataset.sku || null,
    title:     btn.dataset.title || null,
    handle:    btn.dataset.handle || null,
    imageUrl:  fallbackImg,
    quantity:  1
  };
  const item = _ensureSkuVariant(raw, btn, { warn: true });

  basket.push(item);
  setBasket(basket);
  _auditBasket('addOrRemove:add');
  toast(`“${item.title || item.handle || 'Item'}” added`, basket.length);

  if (!IS_EDITOR && !isRentalCartPage()) {
    if (IS_DESKTOP()) openDrawer();
    else if (MOBILE_UI_MODE === 'sheet') openDrawer();
  }
}
 else {
      basket.splice(idx, 1);
      setBasket(basket);
      toast('Removed', basket.length);
      if (!basket.length) closeDrawer();
    }

    updateAllUI();
    updateDrawer();
    updateBackdropInteractivity();
    pinDrawerIfDesktopHasItems();
  };

  // Delegated clicks for remove & add buttons
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('.rd-remove');
    if (rm) {
      const id = rm.dataset.id;
      const basket = getBasket().filter(i => i.id !== id);
      setBasket(basket);
      updateAllUI();
      updateDrawer();
      if (!basket.length) closeDrawer();
      updateBackdropInteractivity();
      return;
    }
    const btn = e.target.closest('.rental-add');
    if (!btn) return;
    addOrRemove(btn);
  });

  /* ---------------- MutationObserver (debounced & scoped) ---------------- */
  const root = document.querySelector('#MainContent') || document.body;
  let scheduled = false;
  const mo = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      mo.disconnect();
      clusterActions();
      paintAddButtons();
      refreshHeaderCounters();
      updateSticky();
      updateDrawer();
      updateBackdropInteractivity();
      mo.observe(root, { childList: true, subtree: true });
    });
  });

  /* ---------------- init & cross-tab ---------------- */
  const updateAllUI = () => {
    paintAddButtons();
    refreshHeaderCounters();
    updateSticky();
  };

const init = () => {
  // Only setup drawer on desktop
  if (IS_DESKTOP() && !isRentalCartPage()) {
    ensureDrawer();
    document.body.classList.add('rental-push'); // Prep for push
  }
  
  // Ensure closed state
  closeDrawer();
  
  clusterActions();
  updateAllUI();
  
  if (IS_DESKTOP() && !isRentalCartPage()) {
    updateDrawer();
    pinDrawerIfDesktopHasItems();
  }
  
  updateBackdropInteractivity();
  
  try { mo.observe(root, { childList: true, subtree: true }); } catch {}
};

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Keep counts in sync across tabs
  window.addEventListener('storage', (ev) => {
    if (ev.key !== STORAGE_KEY) return;
    updateAllUI();
    updateDrawer();
    if (!getBasket().length) closeDrawer();
    updateBackdropInteractivity();
  });

  // Open from utility bar (desktop drawer; mobile goes to page)
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a.utility-bar__menu-link');
    if (a && /Rental Cart/i.test(a.textContent || '')) {
      e.preventDefault();
      if (IS_DESKTOP()) openDrawer();
      else if (MOBILE_UI_MODE === 'sheet') openDrawer();
      else window.location.href = RENTAL_CART_URL;
    }
  }, true);

  // export for debugging
  window.GPERental = {
    getBasket, setBasket, totalQty,
    refreshHeaderCounters, updateSticky, clusterActions, paintAddButtons,
    openDrawer, closeDrawer, updateDrawer
  };
})();
